import { boundaryTextAnchor, createSvgEl, getCartesianAxisLayout } from '@learnforge/demo-render';
import { niceTickStep } from '@learnforge/demo-math/numeric';

export interface AxesOptions {
  xDomain: [number, number];
  yDomain: [number, number];
  width: number;
  height: number;
  xTickStep?: number;
  yTickStep?: number;
  showGrid?: boolean;
}

export interface AxesHandle {
  group: SVGGElement;
  toScreenX(x: number): number;
  toScreenY(y: number): number;
  destroy(): void;
}

export function createAxes(parent: SVGGElement, opts: AxesOptions): AxesHandle {
  const [xMin, xMax] = opts.xDomain;
  const [yMin, yMax] = opts.yDomain;
  const w = opts.width;
  const h = opts.height;
  const xStep = opts.xTickStep ?? niceTickStep(xMax - xMin, 10);
  const yStep = opts.yTickStep ?? niceTickStep(yMax - yMin, 10);

  const toScreenX = (x: number): number => ((x - xMin) / (xMax - xMin)) * w;
  const toScreenY = (y: number): number => h - ((y - yMin) / (yMax - yMin)) * h;

  const root = createSvgEl('g', { class: 'fn-axes' });
  parent.appendChild(root);

  if (opts.showGrid) {
    const grid = createSvgEl('g', { class: 'fn-axis-grid' });
    for (let x = Math.ceil(xMin / xStep) * xStep; x <= xMax; x += xStep) {
      const sx = toScreenX(x);
      const line = createSvgEl('line', {
        x1: sx,
        y1: 0,
        x2: sx,
        y2: h,
        class: 'fn-axis-gridline',
      });
      grid.appendChild(line);
    }
    for (let y = Math.ceil(yMin / yStep) * yStep; y <= yMax; y += yStep) {
      const sy = toScreenY(y);
      const line = createSvgEl('line', {
        x1: 0,
        y1: sy,
        x2: w,
        y2: sy,
        class: 'fn-axis-gridline',
      });
      grid.appendChild(line);
    }
    root.appendChild(grid);
  }

  const axisLayout = getCartesianAxisLayout(opts.xDomain, opts.yDomain);
  const xAxisY = toScreenY(axisLayout.xAxis.value);
  const yAxisX = toScreenX(axisLayout.yAxis.value);
  root.appendChild(
    createSvgEl('line', { x1: 0, y1: xAxisY, x2: w, y2: xAxisY, class: 'fn-axis-line' }),
  );
  root.appendChild(
    createSvgEl('line', { x1: yAxisX, y1: 0, x2: yAxisX, y2: h, class: 'fn-axis-line' }),
  );

  for (let x = Math.ceil(xMin / xStep) * xStep; x <= xMax; x += xStep) {
    if (Math.abs(x) < 1e-9) continue;
    const sx = toScreenX(x);
    root.appendChild(
      createSvgEl('line', {
        x1: sx,
        y1: xAxisY + axisLayout.xAxis.tickOffsets[0] * 4,
        x2: sx,
        y2: xAxisY + axisLayout.xAxis.tickOffsets[1] * 4,
        class: 'fn-axis-tick',
      }),
    );
    const label = createSvgEl('text', {
      x: sx,
      y: xAxisY + axisLayout.xAxis.labelDirection * 16,
      class: 'fn-axis-label',
      'text-anchor': boundaryTextAnchor(sx, 0, w),
      'data-axis': 'x',
    });
    label.textContent = String(Number(x.toFixed(4)));
    root.appendChild(label);
  }
  for (let y = Math.ceil(yMin / yStep) * yStep; y <= yMax; y += yStep) {
    if (Math.abs(y) < 1e-9) continue;
    const sy = toScreenY(y);
    root.appendChild(
      createSvgEl('line', {
        x1: yAxisX + axisLayout.yAxis.tickOffsets[0] * 4,
        y1: sy,
        x2: yAxisX + axisLayout.yAxis.tickOffsets[1] * 4,
        y2: sy,
        class: 'fn-axis-tick',
      }),
    );
    const label = createSvgEl('text', {
      x: yAxisX + axisLayout.yAxis.labelDirection * 8,
      y: sy + 4,
      class: 'fn-axis-label',
      'text-anchor': axisLayout.yAxis.labelAnchor,
      'data-axis': 'y',
    });
    label.textContent = String(Number(y.toFixed(4)));
    root.appendChild(label);
  }

  return {
    group: parent,
    toScreenX,
    toScreenY,
    destroy() {
      root.remove();
    },
  };
}
